import { DetailedHTMLProps, ImgHTMLAttributes, useEffect, useRef, useState } from 'react';
import { getCornerStyleClassname, getCornerStyleTopClassname, IconType, Ratio, Size } from '../../../constants';
import classNames from 'classnames';
import { referralApi, userApi, productApi, themeApi, imageApi } from '../../../api/service';
import { getSizeClasses, ImageId } from './image.constants';
import { PlaceholderMedia } from './PlaceholderMedia';
import { LoadingRing } from '../loaders/LoadingRing';
import { Color } from '../../../context/theme/ThemeConstants';
import { ThemePrimary } from '../../../context/theme/ThemeConstantsUpdated';
import { Dialog } from '../dialogs/Dialog';
import { IconButton } from '../buttons/IconButton';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import { ButtonType } from '../buttons/ButtonType';

const { primaryBackgroundMuted } = ThemePrimary;

export const Image = (props: Props) => {
  const {
    size,
    ratio,
    imageId,
    sizeClass,
    iconSizeClass,
    logoClass,
    forceLoading,
    srcOverride,
    removeBorder,
    isHoverable,
    PlaceholderIcon,
    cornerStyle,
    cornerTopStyle,
    isVibrant,
    isSecondary,
    colorOverride,
    isInverted,
    wrapperClassName,
    loadingSize,
    lightBoxProps,
    onClick,
    debug: _debug,
    ...rest
  } = props;

  const referralImageId = imageId.referralImageId;
  const userImageId = imageId.userImageId;
  const productImageId = imageId.productImageId;
  const backgroundThemeId = imageId.backgroundTheme?.themeId;
  const backgroundSubThemeId = imageId.backgroundTheme?.subThemeId;

  const prevReferralImageIdRef = useRef<string | undefined>(referralImageId);
  const prevUserImageIdRef = useRef<string | undefined>(userImageId);
  const prevProductImageIdRef = useRef<string | undefined>(productImageId);
  const prevBackgroundThemeIdRef = useRef<string | undefined>(backgroundThemeId);
  const prevBackgroundSubThemeIdRef = useRef<string | undefined>(backgroundSubThemeId);
  const prevImageSizeRef = useRef<Size>(size);
  const prevImageRatioRef = useRef<Ratio>(ratio);
  const prevSrcOverrideRef = useRef<string | undefined>(srcOverride);
  const sizeClasses = sizeClass || getSizeClasses({ size, ratio });
  const [src, setSrc] = useState<string>();
  const [hasError, setHasError] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState<boolean>();

  const isBusted = !isLoading && hasError;
  const canOpenLightBox = lightBoxProps != null && !isLoading && !isBusted;
  const lightBoxImageProps =
    lightBoxProps == null
      ? undefined
      : ({ ...props, ...lightBoxProps, lightBoxProps: undefined, onClick: undefined, debug: undefined } as Props);

  const dialogSize =
    lightBoxProps?.size === Size.lg || lightBoxProps?.size === Size.xl
      ? (lightBoxProps.size as Size.lg | Size.xl)
      : Size.xl;

  useEffect(() => {
    const prevReferralImageId = prevReferralImageIdRef.current;
    const prevUserImageId = prevUserImageIdRef.current;
    const prevProductImageId = prevProductImageIdRef.current;
    const prevBackgroundThemeId = prevBackgroundThemeIdRef.current;
    const prevBackgroundSubThemeId = prevBackgroundSubThemeIdRef.current;
    const prevSize = prevImageSizeRef.current;
    const prevRatio = prevImageRatioRef.current;

    const prevSrcOverride = prevSrcOverrideRef.current;
    const hasImageSizeChanged = size !== prevSize;
    const hasImageRatioChanged = ratio !== prevRatio;
    const hasSrcOverrideChanged = (srcOverride != null || prevSrcOverride != null) && prevSrcOverride !== srcOverride;

    const noImage =
      srcOverride == null &&
      referralImageId == null &&
      userImageId == null &&
      productImageId == null &&
      backgroundThemeId == null;

    if (referralImageId && (referralImageId !== prevReferralImageId || hasImageSizeChanged || hasImageRatioChanged)) {
      setSrc(referralApi.getReferralImageSrc(referralImageId, size, ratio));
      setIsLoading(true);
      setHasError(false);
    } else if (userImageId && (userImageId !== prevUserImageId || hasImageSizeChanged || hasImageRatioChanged)) {
      setSrc(userApi.getUserImageSrc(userImageId, size));
      setIsLoading(true);
      setHasError(false);
    } else if (
      productImageId &&
      (productImageId !== prevProductImageId || hasImageSizeChanged || hasImageRatioChanged)
    ) {
      setSrc(productApi.getProductImageSrc(productImageId, size));
      setIsLoading(true);
      setHasError(false);
    } else if (
      backgroundThemeId &&
      (backgroundThemeId !== prevBackgroundThemeId ||
        backgroundSubThemeId !== prevBackgroundSubThemeId ||
        hasImageSizeChanged ||
        hasImageRatioChanged)
    ) {
      setSrc(themeApi.getBackgroundImageSrc(backgroundThemeId, size, backgroundSubThemeId));
      setIsLoading(true);
      setHasError(false);
    } else if (hasSrcOverrideChanged || (src == null && !hasError)) {
      if (srcOverride != null) {
        setSrc(srcOverride);
        setIsLoading(true);
        setHasError(false);
      } else if (referralImageId != null) {
        setSrc(referralApi.getReferralImageSrc(referralImageId, size, ratio));
        setIsLoading(true);
        setHasError(false);
      } else if (userImageId != null) {
        setSrc(userApi.getUserImageSrc(userImageId, size));
        setIsLoading(true);
        setHasError(false);
      } else if (productImageId != null) {
        setSrc(productApi.getProductImageSrc(productImageId, size));
        setIsLoading(true);
        setHasError(false);
      } else if (backgroundThemeId != null) {
        setSrc(themeApi.getBackgroundImageSrc(backgroundThemeId, size, backgroundSubThemeId));
        setIsLoading(true);
        setHasError(false);
      } else if (noImage) {
        setSrc(imageApi.getPlaceholderSrc(size, ratio));
        setIsLoading(true);
        setHasError(false);
      } else {
        setIsLoading(false);
        setHasError(true);
      }
    }

    prevReferralImageIdRef.current = referralImageId;
    prevUserImageIdRef.current = userImageId;
    prevProductImageIdRef.current = productImageId;
    prevBackgroundThemeIdRef.current = backgroundThemeId;
    prevBackgroundSubThemeIdRef.current = backgroundSubThemeId;
    prevImageSizeRef.current = size;
    prevImageRatioRef.current = ratio;
    prevSrcOverrideRef.current = srcOverride;
  }, [
    size,
    ratio,
    srcOverride,
    referralImageId,
    userImageId,
    productImageId,
    backgroundThemeId,
    backgroundSubThemeId,
    src,
    hasError,
  ]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setSrc(imageApi.getPlaceholderSrc(size, ratio));
    } else {
      setIsLoading(false);
    }
  };

  return isBusted ? (
    <div className={wrapperClassName}>
      <PlaceholderMedia
        ratio={ratio}
        size={size}
        cornerStyle={cornerStyle}
        isHoverable={isHoverable}
        sizeClass={sizeClass}
        iconSizeClass={iconSizeClass}
        logoClass={logoClass}
        PlaceholderIcon={PlaceholderIcon}
        isVibrant={isVibrant}
        isSecondary={isSecondary}
        colorOverride={colorOverride}
        removeBorder={removeBorder}
        isInverted={isInverted}
      />
    </div>
  ) : (
    <>
      <div className={classNames('relative', wrapperClassName)}>
        <img
          loading="lazy"
          {...rest}
          className={classNames(
            [rest.className, sizeClasses],
            'object-cover',
            ratio === Ratio.square && 'aspect-square',
            ratio === Ratio.rectangle && 'aspect-[16/10]',
            canOpenLightBox && 'cursor-zoom-in',
            getCornerStyleClassname(cornerStyle),
            cornerTopStyle ? getCornerStyleTopClassname(cornerTopStyle) : undefined,
            (isLoading || forceLoading) && [
              primaryBackgroundMuted,
              'bg-opacity-50',
              sizeClass || getSizeClasses({ size, ratio, isPlaceholder: true }),
            ]
          )}
          src={src}
          onClick={(e) => {
            onClick?.(e);

            if (!canOpenLightBox || e.defaultPrevented) {
              return;
            }

            setIsLightBoxOpen(true);
          }}
          onError={() => handleError()}
          onLoad={() => {
            setIsLoading(false);
            setHasError(false);
          }}
        />
        {(isLoading || forceLoading) && <LoadingRing wrapperClass="abs-middle" size={loadingSize} />}
      </div>
      {lightBoxImageProps != null && (
        <Dialog
          dialogClassName="relative"
          isOpen={isLightBoxOpen}
          toggle={setIsLightBoxOpen}
          header={null}
          removeToggleButton
        >
          <div className="w-screen max-w-full py-3">
            <Image
              {...lightBoxImageProps}
              wrapperClassName={classNames('w-full', lightBoxImageProps.wrapperClassName)}
              forceLoading={false}
              className="mx-auto"
            />
          </div>
          <div className="absolute -right-5 -top-5">
            <IconButton Icon={XMarkIcon} onClick={() => setIsLightBoxOpen(false)} buttonType={ButtonType.Link} />
          </div>
        </Dialog>
      )}
    </>
  );
};

export type ImageProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  size: Size;
  ratio: Ratio;
  imageId: ImageId;
  sizeClass?: string;
  iconSizeClass?: string;
  logoClass?: string;
  forceLoading?: boolean;
  srcOverride?: string;
  removeBorder?: boolean;
  isHoverable?: boolean;
  PlaceholderIcon?: IconType;
  cornerStyle: Size;
  cornerTopStyle?: Size;
  wrapperClassName?: string;
  loadingSize?: Size;

  isVibrant?: boolean;
  isSecondary?: boolean;
  colorOverride?: Color;
  isInverted?: boolean;
  debug?: boolean;
};

type Props = ImageProps & {
  lightBoxProps?: Partial<Omit<ImageProps, 'imageId'>>;
};
